import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lightbulb, X } from 'lucide-react';
import { Alert, Button, Card, CardContent, Dialog, Label, cn } from '@erp/ui';
import { api, ApiError, lerUltimoErro } from '../lib/api';

/** Funcionalidade usada, por tela (alimenta "queda de uso" no admin). Sem dado pessoal. */
export function funcionalidadeDaRota(caminho: string): string {
  const p = caminho.split('/').filter(Boolean);
  if (!p.length) return 'painel';
  if (p[0] === 'estoque')
    return p[1] === 'novo' ? 'estoque_cadastro' : p[1] === 'importar' ? 'importacao' : 'estoque';
  return (p[0] ?? '').replace(/[^a-z_]/g, '').slice(0, 40) || 'painel';
}

/** Registra a abertura de cada tela uma vez por navegação. Falha em silêncio. */
export function useRegistrarUso() {
  const { pathname } = useLocation();
  useEffect(() => {
    const funcionalidade = funcionalidadeDaRota(pathname);
    void api('/suporte/uso', { method: 'POST', json: { funcionalidade, acao: 'abrir' } }).catch(
      () => undefined,
    );
  }, [pathname]);
}

/**
 * Formulário do chamado com o contexto da tela e o último erro visto (docs/12, Suporte). Quando vem
 * do suporte com IA, já chega com a conversa escrita e ligado à pergunta.
 */
export function FormChamado({
  mensagemInicial = '',
  perguntaSuporteId,
  aoConcluir,
}: {
  mensagemInicial?: string;
  perguntaSuporteId?: string;
  aoConcluir: () => void;
}) {
  const { pathname } = useLocation();
  const [mensagem, setMensagem] = useState(mensagemInicial);
  const erro = lerUltimoErro();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/suporte/chamados', {
        method: 'POST',
        json: {
          mensagem,
          tela: pathname.slice(0, 200),
          ultimoErro: erro,
          contexto: {
            navegador: navigator.userAgent.slice(0, 300),
            versao: import.meta.env.VITE_VERSAO ?? 'dev',
            largura: Math.round(window.innerWidth),
          },
          ...(perguntaSuporteId ? { perguntaSuporteId } : {}),
        },
      }),
    onSuccess: () => {
      setMensagem('');
      void qc.invalidateQueries({ queryKey: ['suporte', 'chamados'] });
    },
  });
  if (m.isSuccess)
    return (
      <div className="flex flex-col gap-3">
        <Alert variant="info">
          Recebemos seu chamado. Respondemos no mesmo dia, até as 22h. Você acompanha a resposta em
          Ajuda.
        </Alert>
        <Button onClick={aoConcluir} className="self-end">
          Fechar
        </Button>
      </div>
    );
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate();
      }}
    >
      <div className="flex flex-col gap-2">
        <Label htmlFor="msg-chamado">O que aconteceu?</Label>
        <textarea
          id="msg-chamado"
          required
          minLength={5}
          maxLength={4000}
          rows={5}
          value={mensagem}
          onChange={(e) => setMensagem(e.target.value)}
          className="rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <p className="text-xs text-muted-foreground">
        Tela: {pathname}
        {erro && ' · inclui o último erro exibido'}
      </p>
      {m.isError && (
        <Alert variant="erro">
          {m.error instanceof ApiError ? m.error.message : 'Não foi possível enviar'}
        </Alert>
      )}
      <Button type="submit" disabled={m.isPending || mensagem.trim().length < 5}>
        Enviar
      </Button>
    </form>
  );
}

/** Abre chamado direto com a equipe (sem passar pelo assistente). */
export function DialogoChamado({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  return (
    <Dialog
      aberto={aberto}
      aoFechar={aoFechar}
      titulo="Falar com o suporte"
      descricao="Enviamos junto a tela em que você está para agilizar a resposta."
    >
      {aberto && <FormChamado aoConcluir={aoFechar} />}
    </Dialog>
  );
}

/** Pesquisa NPS discreta: a API decide quando (a cada 90 dias, nunca em sessão de suporte). */
export function PesquisaNps() {
  const q = useQuery({
    queryKey: ['suporte', 'nps'],
    queryFn: () => api<{ pendente: boolean }>('/suporte/nps'),
    staleTime: Infinity,
  });
  const [nota, setNota] = useState<number | null>(null);
  const [comentario, setComentario] = useState('');
  // "Responder depois" vale por 7 dias neste aparelho (antes voltava a cada tela)
  const [fechado, setFechadoEstado] = useState(() => {
    try {
      return Number(localStorage.getItem('nps_adiado_ate') ?? 0) > Date.now();
    } catch {
      return false;
    }
  });
  const setFechado = (v: boolean) => {
    try {
      if (v) localStorage.setItem('nps_adiado_ate', String(Date.now() + 7 * 86_400_000));
    } catch {
      /* sem armazenamento: vale só nesta sessão */
    }
    setFechadoEstado(v);
  };
  const m = useMutation({
    mutationFn: () =>
      api('/suporte/nps', { method: 'POST', json: { nota, comentario: comentario || null } }),
  });
  if (!q.data?.pendente || fechado) return null;
  if (m.isSuccess)
    return (
      <Alert
        variant="info"
        className="fixed bottom-20 right-4 z-30 w-80 items-center shadow-lg md:bottom-4"
      >
        Obrigado pela resposta!
        <button
          type="button"
          className="ml-auto"
          aria-label="Fechar"
          onClick={() => setFechado(true)}
        >
          <X />
        </button>
      </Alert>
    );
  return (
    // Flutuante no canto: não empurra o conteúdo da tela para baixo
    <Card className="fixed bottom-20 right-4 z-30 w-[26rem] max-w-[calc(100vw-2rem)] shadow-lg md:bottom-4">
      <CardContent className="flex flex-col gap-3 pt-4">
        <div className="flex items-start justify-between gap-2">
          <p id="nps-pergunta" className="text-sm font-medium">
            De 0 a 10, quanto você recomendaria o sistema a outro lojista?
          </p>
          <button
            type="button"
            aria-label="Responder depois"
            onClick={() => setFechado(true)}
            className="text-muted-foreground"
          >
            <X className="size-4" />
          </button>
        </div>
        <div role="radiogroup" aria-labelledby="nps-pergunta" className="flex flex-wrap gap-1">
          {Array.from({ length: 11 }, (_, i) => (
            <button
              key={i}
              type="button"
              role="radio"
              aria-checked={nota === i}
              onClick={() => setNota(i)}
              className={cn(
                'size-7 rounded-md border text-xs tabular-nums',
                nota === i
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'hover:bg-accent',
              )}
            >
              {i}
            </button>
          ))}
        </div>
        {nota !== null && (
          <div className="flex flex-col gap-2 sm:flex-row">
            <label className="flex-1">
              <span className="sr-only">Comentário (opcional)</span>
              <input
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                maxLength={2000}
                placeholder="Quer contar o motivo? (opcional)"
                className="h-9 w-full rounded-md border bg-background px-3 text-sm"
              />
            </label>
            <Button size="sm" onClick={() => m.mutate()} disabled={m.isPending}>
              Enviar
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Tour guiado curto na primeira visita a uma tela. Marcado como visto por usuário. */
export function Tour({ tela, passos }: { tela: string; passos: string[] }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['suporte', 'tours'],
    queryFn: () => api<string[]>('/suporte/tours'),
    staleTime: Infinity,
  });
  const [i, setI] = useState(0);
  const concluir = useMutation({
    mutationFn: () => api(`/suporte/tours/${tela}`, { method: 'PUT' }),
    onMutate: () => qc.setQueryData<string[]>(['suporte', 'tours'], (v) => [...(v ?? []), tela]),
  });
  if (!Array.isArray(q.data) || q.data.includes(tela)) return null;
  const ultimo = i === passos.length - 1;
  // Discreta: uma linha só, para não competir com o conteúdo da tela
  return (
    // Flutuante no canto inferior: a dica não ocupa espaço no topo da tela
    <div
      aria-live="polite"
      className="fixed bottom-20 left-4 right-4 z-30 flex flex-col gap-2 rounded-xl border bg-card p-3 text-sm shadow-lg md:bottom-4 md:left-auto md:right-4 md:w-96"
    >
      <p className="flex gap-2">
        <Lightbulb className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
        <span>
          <span className="sr-only">
            Dica {i + 1} de {passos.length}:{' '}
          </span>
          {passos[i]}
        </span>
      </p>
      <div className="flex items-center justify-end gap-1">
        <span className="mr-auto pl-6 text-xs text-muted-foreground" aria-hidden>
          {i + 1}/{passos.length}
        </span>
        {!ultimo && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-muted-foreground"
            onClick={() => concluir.mutate()}
          >
            Pular
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          className="h-7"
          onClick={() => (ultimo ? concluir.mutate() : setI(i + 1))}
        >
          {ultimo ? 'Entendi' : 'Próxima dica'}
        </Button>
      </div>
    </div>
  );
}
